import React, { useState } from 'react';
import {
  Box,
  CircularProgress,
  Divider,
  Typography,
  Button,
  Chip,
  Paper,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Edit, Plus, Truck, DollarSign, Route, Users } from 'lucide-react';
import { OrganizationBasicInfo } from './OrganizationBasicInfo';
import { OrganizationMembers } from './OrganizationMembers';
import { DeliveryRulesModal } from './DeliveryRulesModal';

export const OrganizationInfo = ({
  user,
  userData,
  organizationData,
  loading: orgLoading,
  updateOrganization,
  createOrganization,
  addMember,
  removeMember,
  updateMember,
  isOwner,
  hasOrganization,
  organizationId,
  getMemberCount,
  getMembers,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [rulesModalOpen, setRulesModalOpen] = useState(false);

  const rules       = organizationData?.rules;
  const members     = organizationData?.members ?? [];
  const priceCount  = Object.keys(rules?.deliveryValue ?? {}).length;
  const directCount = Object.keys(rules?.deliveryForwarding?.directDeliveries ?? {}).length;
  const groupCount  = Object.keys(rules?.deliveryForwarding?.deliveryGroups ?? {}).length;
  const hasRules    = priceCount + directCount + groupCount > 0;

  const handleSaveRules = async (newRules) => {
    await updateOrganization({ rules: newRules });
  };

  if (orgLoading) {
    return (
      <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <OrganizationBasicInfo
        user={user}
        userData={userData}
        organizationData={organizationData}
        updateOrganization={updateOrganization}
        createOrganization={createOrganization}
        isOwner={isOwner}
        hasOrganization={hasOrganization}
      />

      {hasOrganization && (
        <>
          <Divider sx={{ my: 4 }} />

          <OrganizationMembers
            user={user}
            organizationData={organizationData}
            organizationId={organizationId}
            isOwner={isOwner}
            getMembers={getMembers}
            addMember={addMember}
            removeMember={removeMember}
            updateMember={updateMember}
          />

          <Divider sx={{ my: 4 }} />

          {/* Kuljetusmäärittelyt */}
          <Box>
            <Box sx={{
              display: 'flex',
              alignItems: { xs: 'flex-start', sm: 'center' },
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between',
              gap: 1,
              mb: 1,
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Truck size={20} />
                <Typography variant="h6" fontWeight={600}>Kuljetusmäärittelyt</Typography>
                {hasRules && <Chip label="Aktiivinen" size="small" color="success" sx={{ height: 20 }} />}
              </Box>

              {isOwner && (
                <Button
                  variant={hasRules ? 'outlined' : 'contained'}
                  size="small"
                  startIcon={hasRules ? <Edit size={15} /> : <Plus size={15} />}
                  onClick={() => setRulesModalOpen(true)}
                  fullWidth={isMobile}
                >
                  {hasRules ? 'Muokkaa määrittelyjä' : 'Luo määrittely'}
                </Button>
              )}
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Hintasäännöt ja kuljetusohjaukset lähettäjäkohtaisesti
            </Typography>

            {hasRules ? (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {priceCount > 0 && (
                  <Chip icon={<DollarSign size={13} />} label={`${priceCount} hintasääntöä`} size="small" variant="outlined" />
                )}
                {directCount > 0 && (
                  <Chip icon={<Route size={13} />} label={`${directCount} suoraa ohjausta`} size="small" variant="outlined" />
                )}
                {groupCount > 0 && (
                  <Chip icon={<Users size={13} />} label={`${groupCount} ryhmäohjausta`} size="small" variant="outlined" />
                )}
              </Box>
            ) : (
              <Paper variant="outlined" sx={{ p: 3, borderStyle: 'dashed', bgcolor: 'action.hover', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Route size={24} style={{ opacity: 0.3 }} />
                <Typography variant="body2" color="text.disabled">
                  {isOwner
                    ? 'Ei kuljetusmäärittelyjä. Luo ensimmäinen määrittely painamalla "Luo määrittely".'
                    : 'Ei kuljetusmäärittelyjä.'}
                </Typography>
              </Paper>
            )}
          </Box>
        </>
      )}

      <DeliveryRulesModal
        open={rulesModalOpen}
        onClose={() => setRulesModalOpen(false)}
        rules={organizationData?.rules}
        members={members}
        onSave={handleSaveRules}
      />
    </Box>
  );
};